import React from 'react';
import StarIcon from '@mui/icons-material/Star';
import StartBorderIcon from '@mui/icons-material/StarBorder';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    checked: boolean;
    color?: string;
    onChange: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        checked: boolean,
    ) => void;
    className?: string;
}

const FavCheckbox = ({
    checked,
    onChange,
    color = 'text-yellow',
    className,
}: Props) => {
    const { i18next } = useAppContext();

    return (
        // A real button, not a clickable div: this was unreachable by keyboard
        // and unannounced by screen readers, so the star could only be toggled
        // with a mouse. role="switch" + aria-checked is what conveys the on/off
        // state, since the only visual cue is which star icon is drawn.
        //
        // The state comes from the caller and is never copied into this
        // component. It used to seed a useState, which then answered to clicks
        // rather than to the stored favourites: a refused save, a refetch or a
        // change from another device left the star, and so aria-checked,
        // stating a favourite that was not there.
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            // Stable name: aria-checked already carries on/off, so a name that
            // flipped with state announced it twice and contradictorily
            // ("Remove from favorites, switch, on").
            aria-label={i18next.t('General.Favorite.Toggle.Title')}
            onClick={(e) => onChange(e, !checked)}
            className={twMerge(
                'hover:darken-10 flex items-center justify-center gap-x-2 rounded-sm border-none p-1',
                className,
            )}
        >
            {checked ? (
                <StarIcon className={color} aria-hidden={true} />
            ) : (
                <StartBorderIcon className="text-gray-500" aria-hidden={true} />
            )}
        </button>
    );
};

export default FavCheckbox;
